/* webauthnApi — frontend helpers for the LEXIQ biometric (passkey) login.
 *
 * Two flows:
 *   1) enableBiometric() — call once after a normal email+password login.
 *      Generates a device-bound keypair, prompts Face ID / Touch ID,
 *      sends the public key to the server. Private key NEVER leaves
 *      the device's Secure Enclave.
 *
 *   2) loginWithBiometric(email) — on the auth page. Triggers Face ID
 *      and exchanges the signed assertion for a normal user JWT.
 *
 * Encoding bridge: WebAuthn options arrive from the server as a JSON
 * string with base64url fields. The browser needs ArrayBuffer for
 * challenge, user.id, allowCredentials[].id. We convert in-place. */
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// — base64url helpers —
function b64uToBuf(b64u) {
  const pad = "=".repeat((4 - (b64u.length % 4)) % 4);
  const b64 = (b64u + pad).replace(/-/g, "+").replace(/_/g, "/");
  const bin = atob(b64);
  const buf = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) buf[i] = bin.charCodeAt(i);
  return buf.buffer;
}

function bufToB64u(buf) {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

/* Inflate server-sent registration options so the browser API accepts them. */
function decodeRegistrationOptions(opts) {
  return {
    ...opts,
    challenge: b64uToBuf(opts.challenge),
    user: { ...opts.user, id: b64uToBuf(opts.user.id) },
    excludeCredentials: (opts.excludeCredentials || []).map((c) => ({
      ...c, id: b64uToBuf(c.id),
    })),
  };
}

/* Inflate server-sent authentication options. */
function decodeAuthOptions(opts) {
  return {
    ...opts,
    challenge: b64uToBuf(opts.challenge),
    allowCredentials: (opts.allowCredentials || []).map((c) => ({
      ...c, id: b64uToBuf(c.id),
    })),
  };
}

/* Convert browser AuthenticatorAttestationResponse to JSON-serializable. */
function serializeAttestation(cred) {
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      attestationObject: bufToB64u(cred.response.attestationObject),
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
}

/* Convert browser AuthenticatorAssertionResponse to JSON-serializable. */
function serializeAssertion(cred) {
  return {
    id: cred.id,
    rawId: bufToB64u(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: bufToB64u(cred.response.clientDataJSON),
      authenticatorData: bufToB64u(cred.response.authenticatorData),
      signature: bufToB64u(cred.response.signature),
      userHandle: cred.response.userHandle ? bufToB64u(cred.response.userHandle) : null,
    },
    clientExtensionResults: cred.getClientExtensionResults?.() || {},
  };
}

export function isWebAuthnSupported() {
  return (
    typeof window !== "undefined" &&
    !!window.PublicKeyCredential &&
    typeof window.PublicKeyCredential === "function"
  );
}

export async function isPlatformAuthenticatorAvailable() {
  if (!isWebAuthnSupported()) return false;
  try {
    return await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
  } catch {
    return false;
  }
}

/** Register a new biometric credential. Caller must be authenticated. */
export async function enableBiometric({ token, deviceName } = {}) {
  if (!isWebAuthnSupported()) throw new Error("جِهازُك لا يَدعَم بَصمَة الويب");
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  const { data: begin } = await axios.post(`${API}/webauthn/register/begin`, {}, { headers });
  const optsRaw = typeof begin.options === "string" ? JSON.parse(begin.options) : begin.options;
  const publicKey = decodeRegistrationOptions(optsRaw);

  const cred = await navigator.credentials.create({ publicKey });
  if (!cred) throw new Error("أُلغيت العَمَلِيَّة");

  const { data: done } = await axios.post(
    `${API}/webauthn/register/complete`,
    {
      credential: serializeAttestation(cred),
      device_name: deviceName || guessDeviceName(),
      user_agent: navigator.userAgent || "",
    },
    { headers },
  );
  return done;
}

/** Authenticate with a previously-registered credential. Returns {token, user}. */
export async function loginWithBiometric(email) {
  if (!isWebAuthnSupported()) throw new Error("جِهازُك لا يَدعَم بَصمَة الويب");
  const { data: begin } = await axios.post(`${API}/webauthn/auth/begin`, { email });
  const optsRaw = typeof begin.options === "string" ? JSON.parse(begin.options) : begin.options;
  const publicKey = decodeAuthOptions(optsRaw);

  const cred = await navigator.credentials.get({ publicKey });
  if (!cred) throw new Error("أُلغيت العَمَلِيَّة");

  const { data } = await axios.post(`${API}/webauthn/auth/complete`, {
    user_id: begin.user_id,
    credential: serializeAssertion(cred),
  });
  return data;  // { token, user }
}

/** Devices list and revocation for the My Devices page. */
export async function listBiometricDevices(token) {
  const { data } = await axios.get(`${API}/webauthn/devices`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return data.devices || [];
}

export async function revokeBiometricDevice(token, credentialIdB64) {
  const { data } = await axios.delete(
    `${API}/webauthn/devices/${encodeURIComponent(credentialIdB64)}`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  return data;
}

/* Best-effort label for the current device. Falls back to the UA. */
function guessDeviceName() {
  const ua = (navigator.userAgent || "").toLowerCase();
  if (/iphone/.test(ua)) return "iPhone";
  if (/ipad/.test(ua)) return "iPad";
  if (/android/.test(ua)) return "Android";
  if (/macintosh|mac os x/.test(ua)) return "Mac";
  if (/windows/.test(ua)) return "Windows";
  if (/linux/.test(ua)) return "Linux";
  return "جِهاز ويب";
}
